import styled from "styled-components"
import * as PropTypes from "prop-types"
import { sendEvent } from "scripts/GoogleAnalytics"
import { useCallback, useEffect } from "react"
import useEmbla from "hooks/useEmbla"
import Dots from "components/Slider/Dots"
import { Viewport } from "components/styled/SliderBase"
import { color } from "helpers/styled"

const Wrapper = styled.div`
  margin: 25px -25px 0;

  svg {
    color: ${color("deepPink")};
  }
`

const Track = styled.div`
  display: flex;
  align-items: stretch;
  padding-bottom: 25px;
`

function PostCarousel({ posts, page, renderPost }) {
  /**
   * @type import('embla-carousel').EmblaOptionsType
   */
  const emblaOptions = {
    align: "center",
    watchDrag: posts.length > 1,
    containScroll: "keepSnaps"
  }

  const breakpoints = [
    {
      size: "xl",
      slidesToScroll: 4,
      watchDrag: posts.length > 4
    },
    {
      size: "lg",
      slidesToScroll: 3,
      watchDrag: posts.length > 3
    }
  ]

  const { emblaRef, embla, selectedIndex, scrollSnaps } = useEmbla(emblaOptions, breakpoints)

  const onSlide = useCallback(
    (newIndex, oldIndex) => {
      if (oldIndex === newIndex) return

      sendEvent("Blog carrousel", {
        event_category: page,
        event_label: `Slide ${oldIndex < newIndex ? "ahead" : "back"}`
      })
    },
    [page]
  )

  useEffect(() => {
    if (!embla) return undefined
    const handleSelect = () => onSlide(embla.selectedScrollSnap(), embla.previousScrollSnap())
    embla.on("select", handleSelect)

    return () => {
      embla.off("select", handleSelect)
    }
  }, [embla, onSlide])

  const scrollTo = useCallback(index => embla && embla.scrollTo(index), [embla])

  return (
    <Wrapper>
      <Viewport ref={emblaRef}>
        <Track>{posts.map(renderPost)}</Track>
      </Viewport>
      <Dots selectedIndex={selectedIndex} snaps={scrollSnaps} handleClick={scrollTo} />
    </Wrapper>
  )
}

PostCarousel.propTypes = {
  posts: PropTypes.array.isRequired,
  page: PropTypes.string.isRequired,
  renderPost: PropTypes.func.isRequired
}

export default PostCarousel
